import {
  NodeProps,
  NodeToolbar,
  useReactFlow,
  useStore,
  useStoreApi,
  NodeResizeControl,
  Handle,
  Position,
} from '@xyflow/react'
import { 
  Users, 
  Trash2,
  Ungroup,
  Play,
  Pause,
  Square,
  Settings,
  Activity,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react'
import { memo, useCallback } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/use-toast'

export interface GroupNodeData extends Record<string, unknown> {
  label: string
  description?: string
  isExpanded?: boolean
  childNodes?: string[]
  groupType?: 'sequential' | 'parallel' | 'conditional'
  backgroundColor?: string
  borderColor?: string
  informationClassification?: string
  createdBy?: string
  createdAt?: string
  
  // Enhanced Blueprint capabilities
  blueprintId?: string
  executionState?: {
    status: 'idle' | 'running' | 'completed' | 'error' | 'paused'
    progress: number
    currentStep?: string
    startTime?: number
    endTime?: number
    error?: string
  }
  blueprintInterface?: {
    inputs: BlueprintParameter[]
    outputs: BlueprintParameter[]
    configuration: Record<string, any>
  }
}

interface BlueprintParameter {
  id: string
  name: string
  type: 'string' | 'number' | 'boolean' | 'object' | 'array' | 'file'
  required: boolean
  defaultValue?: any
  description?: string
}

const controlStyle = {
  background: "transparent",
  border: "none",
  zIndex: 1
}

// Resize icon component
function ResizeIcon() {
  return (
    <div
      style={{
        position: "absolute",
        right: 8,
        bottom: 8,
        cursor: "se-resize",
        ...controlStyle
      }}
      className="nodrag nowheel"
    >
      <div className="w-4 h-4 text-gray-400">⋱</div>
    </div>
  )
}

interface IsEqualCompareObj {
  minWidth: number
  minHeight: number
  hasChildNodes: boolean
  childNodesCount: number
}

function isEqual(prev: IsEqualCompareObj, next: IsEqualCompareObj): boolean {
  return (
    prev.minWidth === next.minWidth &&
    prev.minHeight === next.minHeight &&
    prev.hasChildNodes === next.hasChildNodes &&
    prev.childNodesCount === next.childNodesCount
  )
}

function GroupNode({ id, data, selected }: NodeProps & { data: GroupNodeData }) {
  const store = useStoreApi()
  const { deleteElements, setNodes } = useReactFlow()
  const { toast } = useToast()

  // Resize handler similar to NodeBaseContainer
  const onResizeEnd = useCallback((_event: any, resizeData: any) => {
    console.log('Group node resized:', { id, newSize: resizeData })
    // Update the node data with new dimensions
    setNodes((nodes) =>
      nodes.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            width: resizeData.width,
            height: resizeData.height,
            data: {
              ...node.data,
              width: resizeData.width,
              height: resizeData.height
            }
          }
        }
        return node
      })
    )
  }, [id, setNodes])
  // Calculate minimum size based on child nodes
  const { minWidth, minHeight, hasChildNodes } = useStore((store) => {
    const childNodes = Array.from(store.nodeLookup.values()).filter(
      (n) => n.parentId === id
    )
    
    if (childNodes.length === 0) {
      return {
        minWidth: 300,
        minHeight: 200,
        hasChildNodes: false,
        childNodesCount: 0
      }
    }

    // Calculate bounds of child nodes
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    
    childNodes.forEach(node => {
      const x = node.position?.x || 0
      const y = node.position?.y || 0
      const width = node.width || node.measured?.width || 300
      const height = node.height || node.measured?.height || 200
      
      minX = Math.min(minX, x)
      minY = Math.min(minY, y)
      maxX = Math.max(maxX, x + width)
      maxY = Math.max(maxY, y + height)
    })

    // Add padding around child nodes
    const padding = 20
    const rect = {
      x: minX - padding,
      y: minY - padding,
      width: (maxX - minX) + (padding * 2),
      height: (maxY - minY) + (padding * 2)
    }

    return {
      minWidth: Math.max(300, rect.width),
      minHeight: Math.max(200, rect.height),
      hasChildNodes: childNodes.length > 0,
      childNodesCount: childNodes.length
    }
  }, isEqual)

  // Get child nodes separately for display
  const childNodes = Array.from(store.getState().nodeLookup.values()).filter(
    (n) => n.parentId === id
  )
  const onDelete = useCallback(() => {
    // Get all child nodes that belong to this group
    const childNodeIds = Array.from(store.getState().nodeLookup.values())
      .filter((n) => n.parentId === id)
      .map((n) => n.id)

    // Delete the group node and all its children
    const nodesToDelete = [{ id }, ...childNodeIds.map(childId => ({ id: childId }))]
    
    deleteElements({ nodes: nodesToDelete })
    
    const childCount = childNodeIds.length
    toast({
      title: 'Group Deleted',
      description: `Group "${data.label}" and ${childCount} child node${childCount !== 1 ? 's' : ''} deleted`
    })
  }, [id, deleteElements, data.label, toast, store])
  const onDetach = useCallback(() => {
    const childNodeIds = Array.from(store.getState().nodeLookup.values())
      .filter((n) => n.parentId === id)
      .map((n) => n.id)

    if (childNodeIds.length > 0) {
      // Remove parentId from child nodes to ungroup them
      setNodes((nodes) => 
        nodes.map(node => 
          childNodeIds.includes(node.id) 
            ? { ...node, parentId: undefined, extent: undefined }
            : node
        )
      )
      
      toast({
        title: 'Nodes Ungrouped',
        description: `${childNodeIds.length} nodes ungrouped from "${data.label}"`
      })
    }
  }, [id, store, setNodes, toast])
  const getGroupTypeIcon = () => {
    switch (data.groupType) {
      case 'sequential': return '→'
      case 'parallel': return '‖'
      case 'conditional': return '?'
      default: return '●'
    }
  }

  const getGroupTypeColor = () => {
    switch (data.groupType) {
      case 'sequential': return 'bg-blue-100 text-blue-800'
      case 'parallel': return 'bg-green-100 text-green-800'
      case 'conditional': return 'bg-orange-100 text-orange-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  // Blueprint execution controls
  const handleBlueprintRun = useCallback(() => {
    if (!data.blueprintId) return
    
    toast({
      title: 'Blueprint Execution',
      description: `Starting execution of blueprint "${data.label}"`
    })
    
    // TODO: Implement actual blueprint execution
    // This would integrate with the blueprint execution system
  }, [data.blueprintId, data.label, toast])

  const handleBlueprintPause = useCallback(() => {
    toast({
      title: 'Blueprint Paused',
      description: `Execution of "${data.label}" has been paused`
    })
  }, [data.label, toast])

  const handleBlueprintStop = useCallback(() => {
    toast({
      title: 'Blueprint Stopped',
      description: `Execution of "${data.label}" has been stopped`
    })
  }, [data.label, toast])

  const getExecutionStatusIcon = () => {
    const status = data.executionState?.status || 'idle'
    switch (status) {
      case 'running': return <Activity className="h-4 w-4 text-blue-500 animate-pulse" />
      case 'completed': return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'error': return <XCircle className="h-4 w-4 text-red-500" />
      case 'paused': return <Pause className="h-4 w-4 text-yellow-500" />
      default: return <Clock className="h-4 w-4 text-gray-400" />
    }
  }

  const getExecutionStatusColor = () => {
    const status = data.executionState?.status || 'idle'
    switch (status) {
      case 'running': return 'border-blue-500 bg-blue-50'
      case 'completed': return 'border-green-500 bg-green-50'
      case 'error': return 'border-red-500 bg-red-50'
      case 'paused': return 'border-yellow-500 bg-yellow-50'
      default: return ''
    }
  }

  return (
    <div className="relative h-full w-full">
      {/* Input/Output Handles */}
      <Handle type="target" position={Position.Left} style={{ background: '#555' }} />
      <Handle type="source" position={Position.Right} style={{ background: '#555' }} />
        {/* Toolbar for group actions */}
      <NodeToolbar className="nodrag">
        <div className="flex gap-1">
          {/* Blueprint execution controls */}
          {data.blueprintId && (
            <>
              {(!data.executionState || data.executionState.status === 'idle' || data.executionState.status === 'paused') && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleBlueprintRun}
                  className="h-7 text-xs"
                >
                  <Play className="h-3 w-3 mr-1" />
                  Run
                </Button>
              )}
              {data.executionState?.status === 'running' && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleBlueprintPause}
                    className="h-7 text-xs"
                  >
                    <Pause className="h-3 w-3 mr-1" />
                    Pause
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleBlueprintStop}
                    className="h-7 text-xs"
                  >
                    <Square className="h-3 w-3 mr-1" />
                    Stop
                  </Button>
                </>
              )}
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
              >
                <Settings className="h-3 w-3 mr-1" />
                Config
              </Button>
            </>
          )}
          
          <Button
            size="sm"
            variant="outline"
            onClick={onDelete}
            className="h-7 text-xs"
          >
            <Trash2 className="h-3 w-3 mr-1" />
            Delete
          </Button>
          {hasChildNodes && (
            <Button
              size="sm"
              variant="outline"
              onClick={onDetach}
              className="h-7 text-xs"
            >
              <Ungroup className="h-3 w-3 mr-1" />
              Ungroup
            </Button>
          )}
        </div>
      </NodeToolbar>

      {/* Resize control */}
      {selected && (
        <NodeResizeControl
          style={controlStyle}
          minWidth={minWidth}
          minHeight={minHeight}
          onResizeEnd={onResizeEnd}        >
          <ResizeIcon />
        </NodeResizeControl>
      )}

      <div
        className={`group-node min-w-[300px] min-h-[200px] border-2 border-dashed rounded-lg ${selected ? 'ring-2 ring-blue-500' : ''} ${getExecutionStatusColor()}`}
        style={{
          backgroundColor: data.backgroundColor || 'rgba(254, 243, 199, 0.1)', // Much more transparent
          borderColor: data.borderColor || '#f59e0b',
          width: '100%',
          height: '100%',
          position: 'relative'
        }}
      >
        {/* Input/Output Handles */}
        <Handle 
          type="target" 
          position={Position.Left} 
          style={{ background: '#555', left: -6 }} 
        />
        <Handle 
          type="source" 
          position={Position.Right} 
          style={{ background: '#555', right: -6 }} 
        />          {/* Header with minimal height so child nodes can be visible */}
        <div className="absolute top-0 left-0 right-0 bg-white bg-opacity-90 border-b border-gray-200 p-2 rounded-t-lg z-10 pointer-events-none">
          <div className="flex items-center justify-between pointer-events-auto">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-semibold">
                {data.label || 'Blueprint Container'}
              </span>
              {data.blueprintId && getExecutionStatusIcon()}
            </div>
            
            <div className="flex items-center gap-1">
              {data.executionState?.status === 'running' && (
                <Badge className="text-xs bg-blue-100 text-blue-800">
                  {Math.round(data.executionState.progress || 0)}%
                </Badge>
              )}
              {data.groupType && (
                <Badge className={`text-xs ${getGroupTypeColor()}`}>
                  {getGroupTypeIcon()} {data.groupType}
                </Badge>
              )}
              <Badge variant="outline" className="text-xs">
                {childNodes.length} nodes
              </Badge>
            </div>
          </div>
          
          {data.description && (
            <p className="text-xs text-gray-600 mt-1 pointer-events-auto">{data.description}</p>
          )}
          
          {/* Execution progress bar */}
          {data.executionState?.status === 'running' && (
            <div className="mt-2 pointer-events-auto">
              <div className="w-full bg-gray-200 rounded-full h-1">
                <div 
                  className="bg-blue-500 h-1 rounded-full transition-all duration-300" 
                  style={{ width: `${data.executionState.progress || 0}%` }}
                />
              </div>
              {data.executionState.currentStep && (
                <p className="text-xs text-gray-500 mt-1">
                  Current: {data.executionState.currentStep}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Empty space for child nodes - this is where ReactFlow will render child nodes */}
        <div className="pt-12 p-4 h-full relative" style={{ zIndex: 1 }}>
          {!hasChildNodes && (
            <div className="flex items-center justify-center h-full text-xs text-gray-500 italic pointer-events-none">
              No child nodes - drag nodes here to group them
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default memo(GroupNode)
